import React, { useState, useEffect, useCallback } from 'react';
import CustomSelect from '@/components/common/CustomSelect';

/**
 * Componente para configurar horarios especiales de un solo día
 * Usado para festivos y fechas especiales
 */
export const HorarioEspecialDia = ({ 
  defaultValue = null,
  onChange = () => {} 
}) => {
  // Estado inicial del horario
  const [horario, setHorario] = useState({
    mañana: { 
      activo: true, 
      inicio: "09:00", 
      fin: "13:00" 
    },
    tarde: { 
      activo: true, 
      inicio: "14:00", 
      fin: "18:00" 
    }
  });

  // Inicializar con defaultValue si está disponible
  useEffect(() => {
    if (defaultValue && typeof defaultValue === 'object') {
      setHorario({
        mañana: defaultValue.mañana || { activo: true, inicio: "09:00", fin: "13:00" },
        tarde: defaultValue.tarde || { activo: true, inicio: "14:00", fin: "18:00" }
      });
    }
  }, []); // Solo en el mount inicial

  // Función memoizada para notificar cambios
  const notifyChange = useCallback((newHorario) => {
    onChange(newHorario);
  }, [onChange]);

  // Alternar turno activo/inactivo
  const toggleTurno = (turno) => {
    setHorario(prev => {
      const newHorario = {
        ...prev,
        [turno]: {
          ...prev[turno],
          activo: !prev[turno].activo
        }
      };
      notifyChange(newHorario);
      return newHorario;
    });
  };

  // Actualizar horario específico
  const updateHorario = (turno, campo, valor) => {
    setHorario(prev => {
      const newHorario = {
        ...prev,
        [turno]: {
          ...prev[turno],
          [campo]: valor
        }
      };
      notifyChange(newHorario);
      return newHorario;
    });
  };

  // Generar opciones de horas
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const h = hour.toString().padStart(2, '0');
      options.push(`${h}:00`);
      options.push(`${h}:30`);
    }
    return options;
  };

  const timeOptions = generateTimeOptions();
  
  // Convertir timeOptions para CustomSelect
  const timeSelectOptions = timeOptions.map(time => ({
    value: time,
    label: time
  }));

  return (
    <div className="w-full">
      {/* Layout vertical en 2 filas */}
      <div className="space-y-3">
        {/* Fila 1: Turno Mañana */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="manana-checkbox"
            checked={horario.mañana.activo}
            onChange={() => toggleTurno('mañana')}
            className="w-4 h-4 text-green-600 focus:ring-green-500 flex-shrink-0"
          />
          <label htmlFor="manana-checkbox" className="text-green-400 font-medium text-sm flex-shrink-0 w-14">
            Mañana
          </label>
          {horario.mañana.activo && (
            <>
              <CustomSelect
                value={horario.mañana.inicio}
                onChange={(e) => updateHorario('mañana', 'inicio', e.target.value)}
                options={timeSelectOptions}
                className="flex-1 text-sm"
              />
              <span className="text-gray-400 text-xs flex-shrink-0 px-2">a</span>
              <CustomSelect
                value={horario.mañana.fin}
                onChange={(e) => updateHorario('mañana', 'fin', e.target.value)}
                options={timeSelectOptions}
                className="flex-1 text-sm"
              />
            </>
          )}
        </div>

        {/* Fila 2: Turno Tarde */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="tarde-checkbox"
            checked={horario.tarde.activo}
            onChange={() => toggleTurno('tarde')}
            className="w-4 h-4 text-orange-600 focus:ring-orange-500 flex-shrink-0"
          />
          <label htmlFor="tarde-checkbox" className="text-orange-400 font-medium text-sm flex-shrink-0 w-14">
            Tarde
          </label>
          {horario.tarde.activo && (
            <>
              <CustomSelect
                value={horario.tarde.inicio}
                onChange={(e) => updateHorario('tarde', 'inicio', e.target.value)}
                options={timeSelectOptions}
                className="flex-1 text-sm"
              />
              <span className="text-gray-400 text-xs flex-shrink-0 px-2">a</span>
              <CustomSelect
                value={horario.tarde.fin}
                onChange={(e) => updateHorario('tarde', 'fin', e.target.value)}
                options={timeSelectOptions}
                className="flex-1 text-sm"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HorarioEspecialDia;